import { CompanyRoleEnumLabels } from "@boundless/bl-api-client/auth/models/CompanyRoleEnumLabels"
import {
  useAuthCompanyInvitationAccept,
  useAuthCompanyInvitationRetrieve,
} from "@boundless/bl-api-client/auth/services"
import { useCommonUserRetrieve } from "@boundless/bl-api-client/common/services"
import { getCustomerCompanyListQueryKey } from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import {
  Link,
  createFileRoute,
  redirect,
  useLocation,
} from "@tanstack/react-router"
import { LoaderCircleIcon } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import { useStore } from "zustand"

import { CompanyInvitationForm } from "@/components/account/CompanyInvitationForm"
import { Button, LoadingButton } from "@/components/ui/button"
import { store } from "@/lib/store"

import type { CompanyInvitationToken } from "@boundless/bl-api-client/auth/models"

export const Route = createFileRoute("/(public)/_account/company-invitation")({
  component: RouteComponent,
  validateSearch: z.object({
    token: z.string().catch(""),
  }),
  beforeLoad: ({ search: { token } }) => {
    if (!token) {
      throw redirect({ to: "/" })
    }
  },
  wrapInSuspense: false,
})

/**
 * States who is being invited, by whom, into what and as what.
 *
 * Rendered in every state, including the signup one: without it a stranger
 * arriving from an email has nothing on screen explaining the form.
 */
function InvitationSummary({
  invitation,
}: {
  invitation: CompanyInvitationToken
}) {
  return (
    <p className="text-center">
      {invitation.invitedByFirstName} invited{" "}
      <span className="font-medium">{invitation.email}</span> to join{" "}
      <span className="font-medium">{invitation.companyName}</span> as{" "}
      <span className="font-medium">
        {CompanyRoleEnumLabels[invitation.companyRole]}
      </span>
      .
    </p>
  )
}

/**
 * Accept for an address that already has an account and is signed in as it.
 *
 * Landing on `/resume` rather than the dashboard root matters: it is what
 * keeps a fresh member off an empty Business Details step, where they would
 * create a second Company instead of joining this one.
 */
function AcceptInvitation({
  invitation,
  token,
}: {
  invitation: CompanyInvitationToken
  token: string
}) {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  const { mutate: accept, isPending } = useAuthCompanyInvitationAccept({
    mutation: {
      onSuccess: async () => {
        store.setState({ activeCompanyUuid: invitation.companyUuid })
        await queryClient.invalidateQueries({
          queryKey: getCustomerCompanyListQueryKey(),
        })
        navigate({ to: "/resume" })
      },
      onError: (error) => {
        toast.error(error.message)
      },
    },
  })

  return (
    <div className="flex flex-col space-y-4">
      <InvitationSummary invitation={invitation} />
      {isPending ? (
        <LoadingButton />
      ) : (
        <Button onClick={() => accept({ token })}>
          Join {invitation.companyName}
        </Button>
      )}
    </div>
  )
}

function RouteComponent() {
  const { token } = Route.useSearch()
  const navigate = Route.useNavigate()
  const location = useLocation()

  const isAuthenticated = useStore(store, (state) => state.isAuthenticated)
  const { data: user, isPending: isUserPending } = useCommonUserRetrieve({
    query: { enabled: isAuthenticated },
  })

  const {
    data: invitation,
    isPending,
    isError,
  } = useAuthCompanyInvitationRetrieve(token)

  if (isPending || (isAuthenticated && isUserPending)) {
    return <LoaderCircleIcon className="text-primary h-10 w-10 animate-spin" />
  }

  if (isError) {
    return (
      <p className="text-center">
        This invitation link is invalid or has expired. Ask whoever invited you
        to send a new one.
      </p>
    )
  }

  const signedInAs = user?.email

  if (!signedInAs) {
    if (!invitation.hasAccount) {
      return (
        <div className="flex flex-col space-y-4">
          <InvitationSummary invitation={invitation} />
          <CompanyInvitationForm
            invitation={invitation}
            token={token}
            onSuccess={() => {
              // Signup creates the account but leaves no session, so the only
              // honest next step is the login page. Setting the active company
              // first (it persists to localStorage) means `/resume` picks up
              // the company just joined rather than whatever was there before.
              store.setState({ activeCompanyUuid: invitation.companyUuid })
              toast.success("Account created. Sign in to continue.")
              navigate({ to: "/login", search: { redirect: "/resume" } })
            }}
          />
        </div>
      )
    }

    return (
      <div className="flex flex-col space-y-4">
        <InvitationSummary invitation={invitation} />
        <Button asChild>
          {/* Carry the token through the login round trip: it lives only in
              this URL, and there is no in-app list of received invitations
              to recover it from. */}
          <Link to="/login" search={{ redirect: location.href }}>
            Sign in to accept
          </Link>
        </Button>
      </div>
    )
  }

  if (signedInAs.toLowerCase() !== invitation.email.toLowerCase()) {
    return (
      <p className="text-center">
        This invitation is for {invitation.email}. You&apos;re signed in as{" "}
        {signedInAs}. Sign out and open the link again to accept it.
      </p>
    )
  }

  return <AcceptInvitation invitation={invitation} token={token} />
}
